import { useEffect, useMemo, useState } from 'react';
import { buildApiUrl } from '../config';

const DEFAULT_FEATURES = {
  home: true,
  about: true,
  bills: true,
  calendar: true,
  politicalProfiles: true,
  localBody: true,
  accounts: true
};

const normalizeFeatures = (features) => ({
  ...DEFAULT_FEATURES,
  ...(features || {})
});

export default function useFeatureControls() {
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [activePresetKey, setActivePresetKey] = useState('custom');
  const [loading, setLoading] = useState(true);
  const isProductionSite =
    typeof window !== 'undefined' &&
    /(^|\.)civicduty\.app$/i.test(window.location.hostname);

  useEffect(() => {
    let cancelled = false;
    const fetchFeatureControls = async (withLoading = false) => {
      try {
        if (withLoading) setLoading(true);
        const response = await fetch(buildApiUrl('/api/feature-controls'));
        if (!response.ok) {
          throw new Error('Failed to fetch feature controls');
        }
        const data = await response.json();
        if (!cancelled) {
          setFeatures(normalizeFeatures(data.features));
          setActivePresetKey(data.activePresetKey || 'custom');
        }
      } catch (_error) {
        if (!cancelled) {
          setFeatures(DEFAULT_FEATURES);
          setActivePresetKey('custom');
        }
      } finally {
        if (!cancelled && withLoading) {
          setLoading(false);
        }
      }
    };

    fetchFeatureControls(true);
    let intervalId = null;
    let handleVisibility = null;
    if (!isProductionSite) {
      intervalId = setInterval(() => {
        fetchFeatureControls(false);
      }, 2000);

      handleVisibility = () => {
        if (!document.hidden) {
          fetchFeatureControls(false);
        }
      };
      document.addEventListener('visibilitychange', handleVisibility);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      if (handleVisibility) document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isProductionSite]);

  return useMemo(
    () => ({
      loading,
      features,
      activePresetKey
    }),
    [loading, features, activePresetKey]
  );
}
